import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles, Workflow, MessageCircleMore } from "lucide-react";
import { Link } from "react-router-dom";
import dashboard from "../../assets/images/dashboard-image.png";

const proof = ["Faster daily operations", "Automated records & workflows", "WhatsApp-ready communication"];

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#f7fbfd]">
      <div className="hero-grid absolute inset-0 opacity-70" />
      <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#bde9fb] blur-3xl opacity-60" />
      <div className="absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-[#dff7ff] blur-3xl opacity-70" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-12 sm:px-6 sm:pb-28 sm:pt-16 lg:grid-cols-[0.92fr_1.08fr] lg:px-8 lg:pb-32 lg:pt-20">
        <div>
          <div className="eyebrow">
            <Sparkles className="h-4 w-4" /> Modern ERP for agricultural & trading businesses
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-6 max-w-3xl text-4xl font-bold tracking-tight text-[#003448] sm:text-6xl lg:text-[68px] lg:leading-[1.04]"
          >
            Run your business.
            <span className="block text-[#00aef0]">Let the software do the routine work.</span>
          </motion.h1>

          <p className="mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
            MarketMind brings records, payments, dashboards, reporting and communication into one clean workspace —
            with automation built into the day-to-day workflow.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/pricing" className="btn-primary px-6 py-3.5">
              Book a Demo <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#product" className="btn-secondary px-6 py-3.5">
              Explore MarketMind
            </a>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            {proof.map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm font-medium text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#00aef0]" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative"
        >
          <div className="dashboard-shell">
            <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#00aef0]" />
                <span className="text-sm font-semibold text-[#003448]">MarketMind Dashboard</span>
              </div>
              <span className="rounded-full bg-[#eaf8fe] px-3 py-1 text-xs font-semibold text-[#007ca9]">Live</span>
            </div>
            <div className="relative overflow-hidden bg-slate-50 p-3 sm:p-5">
              <img src={dashboard} alt="MarketMind dashboard" className="w-full rounded-xl border border-slate-200 bg-white shadow-lg" />
              <div className="absolute bottom-8 left-6 rounded-2xl border border-white/80 bg-white/95 p-4 shadow-xl backdrop-blur sm:left-10 sm:w-[250px]">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-[#eaf8fe] p-2 text-[#00aef0]"><Workflow className="h-5 w-5" /></div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Automation</p>
                    <p className="text-sm font-bold text-[#003448]">Records synced automatically</p>
                  </div>
                </div>
              </div>
              <div className="absolute right-6 top-7 hidden rounded-2xl border border-white/80 bg-[#003448]/95 p-4 text-white shadow-xl sm:block">
                <div className="flex items-center gap-2">
                  <MessageCircleMore className="h-5 w-5 text-[#7ddcff]" />
                  <span className="text-sm font-semibold">WhatsApp workflow active</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-lg sm:block">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#003448]"><Sparkles className="h-4 w-4 text-[#00aef0]" /> One system. Less manual work.</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
